// frontend/src/pages/games/SnakeGame.tsx

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSnakeGame } from '../../hooks/games/useSnakeGame';
import { useGameSessionTracking } from '../../hooks/games/useGameSessionTracking';
import { SnakeBoard } from '../../components/games/snake/SnakeBoard';
import { SnakeControls } from '../../components/games/snake/SnakeControls';
import { GameCompleteModal } from '../../components/games/GameCompleteModal';
import '../../styles/games.css';
import '../../styles/games-snake.css';

const GAME_ID = 'snake';
const INITIAL_SNAKE_LENGTH = 3;

const BUFF_LABELS: Record<string, { emoji: string; label: string }> = {
  'double-score': { emoji: '⭐', label: '2x Skor' },
  'speed-boost': { emoji: '⚡', label: 'Hız' },
  'slow-motion': { emoji: '🐢', label: 'Yavaş' },
};

export function SnakeGamePage() {
  const navigate = useNavigate();
  const {
    gridSize,
    snake,
    food,
    specialItem,
    score,
    lives,
    shields,
    buffs,
    isGameOver,
    setDirection,
    restart,
  } = useSnakeGame();

  const { recordCompletion, resetRoundTimer } = useGameSessionTracking(GAME_ID);
  const recordedRef = useRef(false);
  const [xpEarned, setXpEarned] = useState<number | null>(null);

  useEffect(() => {
    if (isGameOver && !recordedRef.current) {
      recordedRef.current = true;
      const fruitsEaten = Math.max(0, snake.length - INITIAL_SNAKE_LENGTH);
      const xpAmount = Math.round(score / 10); // Snake: skora dayalı XP
      const { xpAwarded } = recordCompletion({
        score,
        cumulativeDeltas: { fruitsEaten },
        xpAmount,
      });
      setXpEarned(xpAwarded);
    }
    if (!isGameOver) {
      recordedRef.current = false;
    }
  }, [isGameOver, snake.length, score, recordCompletion]);

  const handleRestart = () => {
    resetRoundTimer();
    setXpEarned(null);
    restart();
  };

  return (
    <div className="game-center-page snake-game-page">
      <header className="game-center-header">
        <div>
          <h1>🐍 Yılan</h1>
          <p>Ok tuşları, WASD veya dokunmatik kontrollerle yönlendir!</p>
        </div>
        <Link to="/games" className="game-card-btn secondary sm">
          ← Oyun Merkezi
        </Link>
      </header>

      <div className="snake-toolbar">
        <div className="snake-stats">
          <span>🏆 Skor: {score}</span>
          <span>❤️ Can: {lives}</span>
          <span>🛡 Kalkan: {shields}</span>
        </div>

        {buffs.length > 0 && (
          <div className="snake-buffs">
            {buffs.map((buff) => {
              const info = BUFF_LABELS[buff.type];
              if (!info) return null;
              return (
                <span key={buff.type} className="snake-buff-badge">
                  {info.emoji} {info.label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <SnakeBoard gridSize={gridSize} snake={snake} food={food} specialItem={specialItem} />

      <SnakeControls onDirection={setDirection} />

      {isGameOver && (
        <GameCompleteModal
          emoji="💀"
          title="Oyun Bitti"
          message={`Final Skorun: ${score}`}
          xpEarned={xpEarned ?? undefined}
          onPlayAgain={handleRestart}
          onReturnToCenter={() => navigate('/games')}
        />
      )}
    </div>
  );
}

